import type { PropertiesData, ReadonlyPropertiesData } from '@/core/annotations'
import type { AnnotationProperties } from '@/modules/Editor/models/annotation/types'
import type { TeamPropertyFrameIndex } from '@/backend/darwin/teamProperties'
import { STATIC_PROPERTY_FRAME_NAME } from '@/backend/darwin/teamProperties'

/**
 * Returns the annotation.properties data filtering out frameIndexes that are null
 */
export const getAnnotationPropertiesWithValidData = (
  properties: AnnotationProperties,
): AnnotationProperties => {
  const validFrames: AnnotationProperties = {}
  for (const frameIndex in properties) {
    const propertiesData = properties[Number(frameIndex)]
    // When properties are removed from a frame, they are set to `null` and we need to remove them
    if (propertiesData !== null) {
      validFrames[Number(frameIndex)] = propertiesData
    }
  }
  return validFrames
}

/**
 * Interpolates frame level properties without considering global properties.
 *
 * Returns a fresh outer object whose inner arrays alias `allProperties`;
 * mutating callers must widen via `cloneMutablePropertiesData`.
 */
export const interpolateFrameLevelProperties = (
  frameIndex: number,
  allProperties: AnnotationProperties,
): ReadonlyPropertiesData => {
  const previousFrames = Object.keys(allProperties).filter(
    (index) => index != 'global' && Number(index) <= frameIndex,
  )
  const activeFrameIndex = previousFrames.reduce((a, b) => Math.max(a, Number(b)), -Infinity)

  const interpolatedProperties: PropertiesData = {}

  // Object.keys returns indices in ascending order
  for (const frameIndex of Object.keys(allProperties)) {
    if (activeFrameIndex < Number(frameIndex)) {
      break
    }

    const currentFrameProperties = allProperties[Number(frameIndex)]

    if (!currentFrameProperties) {
      break
    }

    // null entries on a later frame must not clear an earlier value: skip
    // them while merging so previously-set keys survive.
    for (const propertyId of Object.keys(currentFrameProperties)) {
      const value = currentFrameProperties[propertyId]
      if (value === null) continue
      interpolatedProperties[propertyId] = value
    }
  }

  return interpolatedProperties
}

/**
 * Ascending merge of an annotation's frame-level properties, precomputed so a
 * frame lookup is a binary search. `interpolateFrameLevelProperties` is
 * O(keyframes) per call, so resolving every keyframe of an annotation — what
 * the timeline missing-property markers do — is otherwise quadratic.
 */
export type FrameLevelPropertyIndex = {
  /** Keyframe indices, ascending, truncated where the linear merge stops. */
  readonly frames: readonly number[]
  /** `snapshots[i]` is the merge of `frames[0]`…`frames[i]`. */
  readonly snapshots: readonly ReadonlyPropertiesData[]
  /** Frames could not be kept sorted; lookups use the linear path. */
  readonly unusable: boolean
}

export const createFrameLevelPropertyIndex = (
  allProperties: AnnotationProperties,
): FrameLevelPropertyIndex => {
  const frames: number[] = []
  const snapshots: ReadonlyPropertiesData[] = []
  let running: PropertiesData = {}

  for (const key of Object.keys(allProperties)) {
    const frameIndex = Number(key)
    const frameProperties = allProperties[frameIndex]

    // Same stop condition as the linear merge, and where `global` normally
    // lands: `allProperties[Number('global')]` is `allProperties[NaN]`.
    if (!frameProperties) break

    // Array-index keys enumerate first and ascending, so `frames` ends sorted.
    // A live non-index key breaks that — `undo()` in
    // updateVideoAnnotationSegmentsAction writes `properties[Number('global')]`,
    // a literal "NaN" key, and NaN in `frames` fails every comparison.
    if (!Number.isInteger(frameIndex) || frameIndex < 0 || String(frameIndex) !== key) {
      return { frames: [], snapshots: [], unusable: true }
    }

    running = { ...running }
    for (const propertyId of Object.keys(frameProperties)) {
      const value = frameProperties[propertyId]
      if (value === null) continue
      running[propertyId] = value
    }
    frames.push(frameIndex)
    snapshots.push(running)
  }

  return { frames, snapshots, unusable: false }
}

/**
 * Index-backed equivalent of `interpolateFrameLevelProperties`. The returned
 * object is shared between lookups; widen via `cloneMutablePropertiesData` to mutate.
 */
export const interpolateFrameLevelPropertiesFromIndex = (
  frameIndex: number,
  allProperties: AnnotationProperties,
  index: FrameLevelPropertyIndex,
): ReadonlyPropertiesData => {
  if (index.unusable) {
    return interpolateFrameLevelProperties(frameIndex, allProperties)
  }

  const { frames, snapshots } = index
  let low = 0
  let high = frames.length - 1
  let match = -1
  while (low <= high) {
    const mid = (low + high) >> 1
    if ((frames[mid] as number) <= frameIndex) {
      match = mid
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  // Frames past the cut clamp to the last snapshot, matching the linear merge.
  return match === -1 ? {} : (snapshots[match] as ReadonlyPropertiesData)
}

/**
 * Interpolates properties on every level separately.
 * If extendSectionsByGlobalProperties is true, then section level properties are extended
 * by global properties.
 */
export const interpolateProperties = (
  frameIndexOrGlobal: TeamPropertyFrameIndex,
  propertiesPerFrame: AnnotationProperties,
  extendSectionsByGlobalProperties: boolean = true,
): ReadonlyPropertiesData => {
  let interpolatedProperties: PropertiesData = {}

  if (frameIndexOrGlobal !== STATIC_PROPERTY_FRAME_NAME) {
    interpolatedProperties = interpolateFrameLevelProperties(
      frameIndexOrGlobal,
      propertiesPerFrame,
    ) as PropertiesData
  }

  if (frameIndexOrGlobal === STATIC_PROPERTY_FRAME_NAME || extendSectionsByGlobalProperties) {
    // If global values are set for properties, then override the frame value
    const globalPropertiesOverride = propertiesPerFrame[STATIC_PROPERTY_FRAME_NAME]
    if (globalPropertiesOverride) {
      Object.keys(globalPropertiesOverride).forEach((propertyId: string) => {
        interpolatedProperties[propertyId] = globalPropertiesOverride[propertyId]
      })
    }
  }

  return interpolatedProperties
}

/** Index-backed equivalent of `interpolateProperties`. */
export const interpolatePropertiesFromIndex = (
  frameIndexOrGlobal: TeamPropertyFrameIndex,
  propertiesPerFrame: AnnotationProperties,
  index: FrameLevelPropertyIndex,
  extendSectionsByGlobalProperties: boolean = true,
): ReadonlyPropertiesData => {
  if (index.unusable) {
    return interpolateProperties(
      frameIndexOrGlobal,
      propertiesPerFrame,
      extendSectionsByGlobalProperties,
    )
  }

  const isGlobal = frameIndexOrGlobal === STATIC_PROPERTY_FRAME_NAME
  const frameLevelProperties = isGlobal
    ? {}
    : interpolateFrameLevelPropertiesFromIndex(frameIndexOrGlobal, propertiesPerFrame, index)

  if (!isGlobal && !extendSectionsByGlobalProperties) {
    return frameLevelProperties
  }

  const globalPropertiesOverride = propertiesPerFrame[STATIC_PROPERTY_FRAME_NAME]
  if (!globalPropertiesOverride) {
    return frameLevelProperties
  }

  // Snapshots are shared between lookups, so the global overlay goes onto a copy.
  const interpolatedProperties = { ...frameLevelProperties } as PropertiesData
  Object.keys(globalPropertiesOverride).forEach((propertyId: string) => {
    interpolatedProperties[propertyId] = globalPropertiesOverride[propertyId]
  })

  return interpolatedProperties
}
